import { getUiLocale, formatUiDate } from '../utils/uiTranslation';
import { buildInterviewMessage } from '../utils/interviewMessages';
import React, { useState, useEffect } from 'react';
import { ref, get, set, push, update } from 'firebase/database';
import useTeamMembers from '../hooks/useTeamMembers';
import { database } from '../firebase';
import './applicants/Applicants.css';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '../contexts/LanguageContext';
import { emitAtsAnalyticsEvent } from '../services/atsAnalyticsService';
import { buildActorAnalyticsContext, normalizeAnalyticsSource } from '../utils/analyticsEntityFields';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL_PROD;

const BulkInterviewScheduler = ({
  applicants, // Array of applicant objects
  userUID,
  onClose,
  onScheduleSuccess,
  jobTitle = '',
  companyName = '',
  substageTitle = '',
  substageData = null
}) => {
  const { t } = useTranslation('applicants');
  const { isRTL } = useLanguage();
  const [candidateSchedules, setCandidateSchedules] = useState([]);
  const [calendarConnections, setCalendarConnections] = useState([]);
  const [suggestedTimes, setSuggestedTimes] = useState([]);
  const [isLoadingSuggestions, setIsLoadingSuggestions] = useState(false);
  const [isScheduling, setIsScheduling] = useState(false);
  const [scheduleStatus, setScheduleStatus] = useState('');
  const [statusTone, setStatusTone] = useState('info');
  const [actualCompanyName, setActualCompanyName] = useState(companyName || '');
  const { teamMembers } = useTeamMembers(userUID);
  const [selectedCollaborators, setSelectedCollaborators] = useState([]);
  const [duration, setDuration] = useState(60);
  const [interviewTitle, setInterviewTitle] = useState('');
  const [useSmartScheduling, setUseSmartScheduling] = useState(true);

  useEffect(() => {
    loadCalendarConnections();
    loadCompanyData();
    initializeCandidateSchedules();

    // Set duration and interviewers from substage data
    if (substageData) {
      if (substageData.duration) {
        setDuration(substageData.duration);
      }
      if (substageData.interviewers) {
        const interviewerIds = Object.keys(substageData.interviewers).filter(id => substageData.interviewers[id] === true);
        const filteredInterviewerIds = interviewerIds.filter(id => id !== userUID);
        const uniqueInterviewerIds = [...new Set(filteredInterviewerIds)];
        setSelectedCollaborators(uniqueInterviewerIds);
      }
    }

    // Set interview title
    if (substageTitle) {
      setInterviewTitle(`${substageTitle} - ${jobTitle || 'Position'}`);
    } else {
      setInterviewTitle(`Interview - ${jobTitle || 'Position'}`);
    }
  }, []);

  useEffect(() => {
    if (calendarConnections.length > 0 && useSmartScheduling) {
      loadSmartSchedule();
    }
  }, [calendarConnections, duration, applicants.length]);

  const updateStatus = (content, tone = 'info') => {
    setScheduleStatus(content);
    setStatusTone(tone);
  };

  const initializeCandidateSchedules = async () => {
    const schedules = await Promise.all(
      applicants.map(async (applicant) => {
        const applicantData = await loadApplicantData(applicant.id);
        return {
          id: applicant.id,
          name: applicantData.name || applicant.name || applicant.username || 'Candidate',
          email: applicantData.email || applicant.email || '',
          candidateId: applicantData.candidateId || applicant.id,
          roleId: applicantData.roleId || applicant.roleId || applicant.jobId || '',
          ownerRecruiterId: applicantData.ownerRecruiterId || applicant.ownerRecruiterId || '',
          ownerRecruiterEmail: applicantData.ownerRecruiterEmail || applicant.ownerRecruiterEmail || '',
          primarySource: applicantData.primarySource || applicant.primarySource || '',
          sourceCategory: applicantData.sourceCategory || applicant.sourceCategory || '',
          sourceTags: applicantData.sourceTags || applicant.sourceTags || [],
          utmSource: applicantData.utmSource || applicant.utm_source || applicant.utmSource || '',
          connectionKey: applicantData.connectionKey || applicant.connectionKey || '',
          date: '',
          time: '',
          status: 'pending'
        };
      })
    );
    setCandidateSchedules(schedules);
  };

  const loadApplicantData = async (applicantId) => {
    try {
      let applicantInfo = null;

      // Try connection pattern
      let connectionKey = `${userUID}_${applicantId}`;
      let connectionRef = ref(database, `connections/${connectionKey}`);
      let connectionSnapshot = await get(connectionRef);

      if (!connectionSnapshot.exists() && applicantId.startsWith('temp_')) {
        connectionKey = `${userUID}_imported_${applicantId}`;
        connectionRef = ref(database, `connections/${connectionKey}`);
        connectionSnapshot = await get(connectionRef);
      }

      if (connectionSnapshot.exists()) {
        const connectionData = connectionSnapshot.val();
        applicantInfo = {
          name: connectionData.applicantName || connectionData.name || 'Candidate',
          email: connectionData.applicantEmail || connectionData.email,
          candidateId: connectionData.candidateId || applicantId,
          roleId: connectionData.roleId || connectionData.jobId || '',
          ownerRecruiterId: connectionData.ownerRecruiterId || '',
          ownerRecruiterEmail: connectionData.ownerRecruiterEmail || '',
          primarySource: connectionData.primarySource || connectionData.source || '',
          sourceCategory: connectionData.sourceCategory || '',
          sourceTags: connectionData.sourceTags || [],
          utmSource: connectionData.utm_source || connectionData.utmSource || '',
          connectionKey
        };
      }

      // Try people/imported_profiles
      if (!applicantInfo?.email) {
        if (applicantId.startsWith('temp_')) {
          // Try NEW path first
          let importedRef = ref(database, `imported_profiles/${userUID}/${applicantId}`);
          let importedSnapshot = await get(importedRef);

          // Fallback to old path
          if (!importedSnapshot.exists()) {
            importedRef = ref(database, `imported_profiles/${applicantId}`);
            importedSnapshot = await get(importedRef);
          }

          if (importedSnapshot.exists()) {
            const importedData = importedSnapshot.val();
            applicantInfo = {
              name: importedData.name || 'Candidate',
              email: importedData.email,
              candidateId: importedData.candidateId || applicantId,
              roleId: importedData.roleId || '',
              ownerRecruiterId: importedData.ownerRecruiterId || '',
              ownerRecruiterEmail: importedData.ownerRecruiterEmail || '',
              primarySource: importedData.primarySource || importedData.source || '',
              sourceCategory: importedData.sourceCategory || '',
              sourceTags: importedData.sourceTags || [],
              utmSource: importedData.utm_source || importedData.utmSource || ''
            };
          }
        } else {
          const peopleRef = ref(database, `people/${applicantId}`);
          const peopleSnapshot = await get(peopleRef);
          if (peopleSnapshot.exists()) {
            const peopleData = peopleSnapshot.val();
            applicantInfo = {
              name: peopleData.username || peopleData.name || 'Candidate',
              email: peopleData.email
            };
          }
        }
      }

      // Try cloud function for email
      if (!applicantInfo?.email) {
        try {
          const emailResponse = await fetch('https://us-central1-jobseeqr.cloudfunctions.net/getUserEmail', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ uid: applicantId })
          });
          const emailData = await emailResponse.json();
          if (emailData.email) {
            applicantInfo = {
              name: applicantInfo?.name || 'Candidate',
              email: emailData.email
            };
          }
        } catch (error) {
          console.warn('Could not fetch email via cloud function:', error);
        }
      }

      return applicantInfo || { name: 'Candidate', email: 'unknown@example.com' };
    } catch (error) {
      console.error('Error loading applicant data:', error);
      return { name: 'Candidate', email: 'unknown@example.com' };
    }
  };

  const loadCalendarConnections = async () => {
    try {
      const connectionsRef = ref(database, `companies/${userUID}/calendarIntegrations`);
      const snapshot = await get(connectionsRef);

      if (snapshot.exists()) {
        const connections = snapshot.val();
        const connectionsArray = Object.values(connections);
        setCalendarConnections(connectionsArray);
      } else {
        setCalendarConnections([]);
      }
    } catch (error) {
      console.error('Error loading calendar connections:', error);
      setCalendarConnections([]);
    }
  };

  const loadCompanyData = async () => {
    try {
      const companyRef = ref(database, `companies/${userUID}`);
      const snapshot = await get(companyRef);

      if (snapshot.exists()) {
        const companyData = snapshot.val();
        if (companyData.companyName) {
          setActualCompanyName(companyData.companyName);
        }
      }
    } catch (error) {
      console.error('Error loading company data:', error);
    }
  };

  const loadSmartSchedule = async () => {
    if (calendarConnections.length === 0) return;

    setIsLoadingSuggestions(true);

    try {
      const calendarConnection = calendarConnections.find(conn =>
        conn.provider === 'google' || conn.provider === 'outlook'
      );

      if (!calendarConnection) {
        setIsLoadingSuggestions(false);
        return;
      }

      const today = new Date();
      const nextWeek = new Date(today.getTime() + (7 * 24 * 60 * 60 * 1000));

      const requestPayload = {
        user_id: userUID,
        duration_minutes: duration,
        start_date: today.toISOString().split('T')[0],
        end_date: nextWeek.toISOString().split('T')[0],
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        working_hours_start: 9,
        working_hours_end: 17,
        token_data: {
          provider: calendarConnection.provider,
          access_token: calendarConnection.access_token,
          refresh_token: calendarConnection.refresh_token,
          token_uri: calendarConnection.token_uri,
          client_id: calendarConnection.client_id,
          client_secret: calendarConnection.client_secret,
          scopes: calendarConnection.scopes
        }
      };

      const response = await fetch(`${BACKEND_URL}/calendar/suggest-times`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestPayload)
      });

      const data = await response.json();

      if (data.success && data.suggestions.length > 0) {
        setSuggestedTimes(data.suggestions);
        // Auto-assign smart schedule to candidates
        autoAssignSmartSchedule(data.suggestions);
      }
    } catch (error) {
      console.error('Error loading smart schedule:', error);
    } finally {
      setIsLoadingSuggestions(false);
    }
  };

  const autoAssignSmartSchedule = (suggestions) => {
    // Space out interviews optimally - use every other slot to avoid back-to-back
    const optimalSlots = [];
    for (let i = 0; i < suggestions.length && optimalSlots.length < applicants.length; i += 2) {
      optimalSlots.push(suggestions[i]);
    }

    // If we need more slots, fill in the gaps
    if (optimalSlots.length < applicants.length) {
      for (let i = 1; i < suggestions.length && optimalSlots.length < applicants.length; i += 2) {
        optimalSlots.push(suggestions[i]);
      }
    }

    setCandidateSchedules(prev => prev.map((candidate, index) => {
      if (index < optimalSlots.length) {
        const slot = optimalSlots[index];
        const startTime = new Date(slot.start_time);
        const localDate = startTime.toISOString().split('T')[0];
        const localTime = startTime.toTimeString().slice(0, 5);

        return {
          ...candidate,
          date: localDate,
          time: localTime
        };
      }
      return candidate;
    }));
  };

  const updateCandidateSchedule = (candidateId, field, value) => {
    setCandidateSchedules(prev => prev.map(candidate =>
      candidate.id === candidateId
        ? { ...candidate, [field]: value }
        : candidate
    ));
  };

  const scheduleAllInterviews = async () => {
    const messageLanguage = getUiLocale();
    // Validate all candidates have dates and times
    const incomplete = candidateSchedules.filter(c => !c.date || !c.time);
    if (incomplete.length > 0) {
      updateStatus(t('bulkInterview.status.incomplete', { count: incomplete.length }), 'error');
      return;
    }

    if (calendarConnections.length === 0) {
      updateStatus(t('interviewScheduler.status.noCalendar'), 'error');
      return;
    }

    setIsScheduling(true);
    updateStatus(t('bulkInterview.status.scheduling', { count: candidateSchedules.length }));

    try {
      const calendarConnection = calendarConnections.find(conn =>
        conn.provider === 'google' || conn.provider === 'outlook'
      );

      if (!calendarConnection) {
        updateStatus(t('interviewScheduler.status.unsupportedCalendar'), 'error');
        setIsScheduling(false);
        return;
      }

      // Get collaborator emails
      const selectedCollaboratorEmails = selectedCollaborators.map(collabId => {
        const member = teamMembers.find(m => m.uid === collabId);
        return member ? member.email : null;
      }).filter(email => email !== null);

      const uniqueCollaboratorEmails = [...new Set(selectedCollaboratorEmails)];

      // Schedule each interview separately to ensure no mix-ups
      let successCount = 0;
      let failedCandidates = [];

      for (let i = 0; i < candidateSchedules.length; i++) {
        const candidate = candidateSchedules[i];
        updateStatus(t('bulkInterview.status.schedulingSingle', {
          index: i + 1,
          total: candidateSchedules.length,
          name: candidate.name || t('interviewScheduler.candidate')
        }));

        try {
          const startDateTime = new Date(`${candidate.date}T${candidate.time}`);
          const endDateTime = new Date(startDateTime.getTime() + (duration * 60000));

          // CRITICAL: Each candidate gets their own separate attendee list
          const attendeeEmails = [candidate.email, ...uniqueCollaboratorEmails];

          const scheduleRequest = {
            applicant_id: candidate.id,
            user_id: userUID,
            interview_title: `${interviewTitle} - ${candidate.name}`,
            duration_minutes: duration,
            applicant_email: candidate.email,
            applicant_name: candidate.name,
            company_name: actualCompanyName || t('interviewScheduler.company'),
            job_title: jobTitle || t('interviewScheduler.position'),
            start_time: startDateTime.toISOString(),
            end_time: endDateTime.toISOString(),
            timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
            attendee_emails: attendeeEmails,
            token_data: {
              provider: calendarConnection.provider,
              access_token: calendarConnection.access_token,
              refresh_token: calendarConnection.refresh_token,
              token_uri: calendarConnection.token_uri,
              client_id: calendarConnection.client_id,
              client_secret: calendarConnection.client_secret,
              scopes: calendarConnection.scopes
            }
          };

          const scheduleResponse = await fetch(`${BACKEND_URL}/schedule-interview`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify(scheduleRequest)
          });

          const scheduleData = await scheduleResponse.json();

          if (!scheduleData.success) {
            throw new Error(scheduleData.error || 'Failed to schedule interview');
          }

          // Send email notification
          const interviewerNames = selectedCollaborators.map(collabId => {
            const member = teamMembers.find(m => m.uid === collabId);
            return member ? member.name : null;
          }).filter(name => name !== null);

          const interviewerNamesStr = [...new Set(interviewerNames)].join(', ') || t('interviewScheduler.interviewTeam');

          await fetch(`${BACKEND_URL}/send-interview-email`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              companyUID: userUID,
              language: messageLanguage,
              duration,
              applicant_email: candidate.email,
              applicant_name: candidate.name,
              company_name: actualCompanyName || t('interviewScheduler.company'),
              job_title: jobTitle || t('interviewScheduler.position'),
              meeting_link: scheduleData.meeting_link,
              interview_date: formatUiDate(candidate.date, {
                weekday: 'long',
                year: 'numeric',
                month: 'long',
                day: 'numeric'
              }, messageLanguage),
              interview_time: candidate.time,
              interviewer_name: interviewerNamesStr
            })
          });

          // Send chat notification
          try {
            const chatMessage = buildInterviewMessage({ jobTitle, companyName: actualCompanyName, date: candidate.date, time: candidate.time, interviewer: interviewerNamesStr, duration, meetingLink: scheduleData.meeting_link }, messageLanguage);

            let chatConnectionKey = `${userUID}_${candidate.id}`;
            if (candidate.id.startsWith('temp_')) {
              chatConnectionKey = `${userUID}_imported_${candidate.id}`;
            }

            const messagesRef = ref(database, `connections/${chatConnectionKey}/messages`);
            const newMessageRef = push(messagesRef);
            await set(newMessageRef, {
              message: chatMessage,
              user: userUID,
              timestamp: new Date().toISOString(),
              type: 'interview_scheduled'
            });
          } catch (chatError) {
            console.error('Chat notification failed:', chatError);
          }

          // Save to Firebase
          const interviewDateTime = new Date(`${candidate.date}T${candidate.time}`);
          const reminderTime = new Date(interviewDateTime.getTime() - (60 * 60 * 1000));

          const interviewData = {
            applicantId: candidate.id,
            applicantName: candidate.name,
            applicantEmail: candidate.email,
            companyId: userUID,
            companyName: actualCompanyName || t('interviewScheduler.company'),
            jobTitle: jobTitle || t('interviewScheduler.position'),
            title: `${interviewTitle} - ${candidate.name}`,
            date: candidate.date,
            time: candidate.time,
            dateTime: interviewDateTime.toISOString(),
            duration: duration,
            interviewer: interviewerNamesStr,
            meetingLink: scheduleData.meeting_link,
            meetingId: scheduleData.meeting_id,
            notes: '',
            status: 'scheduled',
            createdAt: new Date().toISOString(),
            emailSent: true,
            chatSent: true,
            reminderTime: reminderTime.toISOString(),
            reminderSent: false,
            timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
            collaborators: selectedCollaborators.map(collabId => {
              const member = teamMembers.find(m => m.uid === collabId);
              return member ? {
                uid: member.uid,
                name: member.name,
                email: member.email,
                isOwner: member.isOwner
              } : null;
            }).filter(collab => collab !== null),
            attendeeEmails: attendeeEmails
          };

          const applicantInterviewRef = ref(database, `companies/${userUID}/interviews/${candidate.id}/${scheduleData.meeting_id}`);
          await set(applicantInterviewRef, interviewData);

          // Update connection record
          let updateConnectionKey = `${userUID}_${candidate.id}`;
          if (candidate.id.startsWith('temp_')) {
            updateConnectionKey = `${userUID}_imported_${candidate.id}`;
          }

          const connectionUpdateRef = ref(database, `connections/${updateConnectionKey}`);
          await update(connectionUpdateRef, {
            meetingLink: scheduleData.meeting_link,
            lastInterviewScheduled: new Date().toISOString()
          });

          emitAtsAnalyticsEvent({
            company_uid: userUID,
            event_type: 'interview_scheduled',
            occurred_at: interviewData.createdAt,
            subject: {
              applicantId: candidate.id,
              candidateId: candidate.candidateId || candidate.id,
              applicantType: candidate.id?.startsWith('temp_') ? 'imported' : 'authenticated'
            },
            job: {
              jobId: candidate.roleId || '',
              jobTitle: interviewData.jobTitle || jobTitle || 'Position'
            },
            context: {
              interviewId: scheduleData.meeting_id,
              dateTime: interviewData.dateTime,
              durationMinutes: Number(interviewData.duration) || 0,
              status: interviewData.status || 'scheduled',
              connectionKey: candidate.connectionKey || updateConnectionKey,
              ...buildActorAnalyticsContext({
                actorUserId: userUID,
                ownerRecruiterId: candidate.ownerRecruiterId || '',
                ownerRecruiterEmail: candidate.ownerRecruiterEmail || '',
                ...normalizeAnalyticsSource({
                  utmSource: candidate.utmSource || '',
                  source: candidate.primarySource || '',
                  sourceCategory: candidate.sourceCategory || '',
                  sourceTags: candidate.sourceTags || []
                })
              })
            }
          });

          successCount++;

          // Update candidate status
          setCandidateSchedules(prev => prev.map(c =>
            c.id === candidate.id ? { ...c, status: 'success' } : c
          ));

        } catch (error) {
          console.error(`Failed to schedule interview for ${candidate.name}:`, error);
          failedCandidates.push(candidate.name);
          setCandidateSchedules(prev => prev.map(c =>
            c.id === candidate.id ? { ...c, status: 'failed' } : c
          ));
        }
      }

      // Final status
      if (failedCandidates.length === 0) {
        updateStatus(t('bulkInterview.status.success', { count: successCount }), 'success');

        if (onScheduleSuccess) {
          onScheduleSuccess({ count: successCount });
        }

        setTimeout(() => {
          onClose();
        }, 3000);
      } else {
        updateStatus(
          t('bulkInterview.status.partial', { success: successCount, total: candidateSchedules.length, failed: failedCandidates.join(', ') }),
          'warning'
        );
      }

    } catch (error) {
      console.error('Bulk interview scheduling error:', error);
      updateStatus(t('bulkInterview.status.error', { message: error.message }), 'error');
    } finally {
      setIsScheduling(false);
    }
  };

  const getMinDate = () => {
    return new Date().toISOString().split('T')[0];
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div
        className="modal-content"
        onClick={(e) => e.stopPropagation()}
        style={{ maxWidth: '900px', maxHeight: '90vh', direction: isRTL ? 'rtl' : 'ltr', fontFamily: isRTL ? 'Alexandria, Montserrat' : 'Montserrat' }}
      >
        {/* Header */}
        <div className="modal-header">
          <h2 style={{ fontSize: '20px', fontWeight: '600' }}>
            <i className="fa-solid fa-calendar-plus" style={{ marginRight: '10px', color: '#568ded' }}></i>
            {t('bulkInterview.title', { count: candidateSchedules.length })}
          </h2>
          <div className="close" onClick={onClose}>&times;</div>
        </div>

        <div className="modal-page-container" style={{ overflowY: 'auto', maxHeight: 'calc(90vh - 200px)', padding: '0 24px' }}>
          {/* Calendar Connection Status */}
          {calendarConnections.length === 0 && (
            <div style={{
              backgroundColor: '#F8D7DA',
              padding: '16px 20px',
              borderRadius: '12px',
              marginBottom: '24px',
              border: '1px solid #F5C6CB'
            }}>
              <p style={{ margin: 0, color: 'var(--app-tone-721c24, #721C24)', fontWeight: '600' }}>
                <i className="fa-solid fa-exclamation-triangle" style={{ marginRight: '8px' }}></i>
                {t('interviewScheduler.noCalendar')}
              </p>
            </div>
          )}

          {/* Smart Scheduling Toggle */}
          {calendarConnections.length > 0 && (
            <div style={{
              marginBottom: '24px',
              padding: '16px 20px',
              backgroundColor: '#F8F9FA',
              borderRadius: '12px',
              border: '2px solid #568ded'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <div>
                  <h3 style={{ fontSize: '16px', fontWeight: '600', marginBottom: '4px', color: 'var(--app-heading, #031128)' }}>
                    <i className="fa-solid fa-sparkles" style={{ marginRight: '8px', color: '#568ded' }}></i>
                    {useSmartScheduling ? t('bulkInterview.smartSchedulingActive') : t('bulkInterview.smartScheduling')}
                  </h3>
                  <p style={{ margin: 0, fontSize: '13px', color: '#666' }}>
                    {t('bulkInterview.smartSchedulingDesc')}
                  </p>
                </div>
                {isLoadingSuggestions && (
                  <i className="fa-solid fa-spinner fa-spin" style={{ fontSize: '20px', color: '#568ded' }}></i>
                )}
              </div>
            </div>
          )}

          {/* Candidate Schedule List - Hidden while loading suggestions */}
          {!isLoadingSuggestions && (
            <div style={{ marginBottom: '24px' }}>
              <h3 style={{ fontSize: '16px', fontWeight: '600', marginBottom: '16px', color: 'var(--app-heading, #031128)' }}>
                <i className="fa-solid fa-users" style={{ marginRight: '8px', color: '#568ded' }}></i>
                {t('bulkInterview.interviewSchedule')}
              </h3>

              {candidateSchedules.map((candidate, index) => (
                <div
                  key={candidate.id}
                  style={{
                    backgroundColor: '#FFFFFF',
                    border: '2px solid #E0E0E0',
                    borderRadius: '12px',
                    padding: '16px',
                    marginBottom: '12px',
                    transition: 'all 0.2s ease'
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', marginBottom: '12px' }}>
                    <div style={{
                      width: '32px',
                      height: '32px',
                      borderRadius: '50%',
                      backgroundColor: candidate.status === 'success' ? '#D4EDDA' :
                        candidate.status === 'failed' ? '#F8D7DA' : '#568ded',
                      color: candidate.status === 'success' ? 'var(--app-tone-155724, #155724)' :
                        candidate.status === 'failed' ? 'var(--app-tone-721c24, #721C24)' : 'var(--app-text, #FFFFFF)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontWeight: '600',
                      fontSize: '14px',
                      marginRight: '12px'
                    }}>
                      {candidate.status === 'success' ? '✓' :
                        candidate.status === 'failed' ? '✗' : index + 1}
                    </div>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: '600', fontSize: '15px', color: 'var(--app-heading, #031128)', marginBottom: '2px' }}>
                        {candidate.name}
                      </div>
                      <div style={{ fontSize: '13px', color: '#666' }}>
                        {candidate.email}
                      </div>
                    </div>
                  </div>

                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                    <div>
                      <label style={{ display: 'block', marginBottom: '6px', fontWeight: '600', fontSize: '13px', color: 'var(--app-heading, #031128)' }}>
                        {t('interviewScheduler.dateLabel')}
                      </label>
                      <input
                        type="date"
                        value={candidate.date}
                        onChange={(e) => updateCandidateSchedule(candidate.id, 'date', e.target.value)}
                        min={getMinDate()}
                        disabled={candidate.status === 'success'}
                        style={{
                          width: '100%',
                          padding: '10px 12px',
                          border: '2px solid #E0E0E0',
                          borderRadius: '8px',
                          fontSize: '13px',
                          fontFamily: 'Montserrat',
                          color: 'var(--app-heading, #031128)',
                          backgroundColor: candidate.status === 'success' ? '#F8F9FA' : '#FFFFFF',
                          boxSizing: 'border-box'
                        }}
                      />
                    </div>

                    <div>
                      <label style={{ display: 'block', marginBottom: '6px', fontWeight: '600', fontSize: '13px', color: 'var(--app-heading, #031128)' }}>
                        {t('interviewScheduler.timeLabel')}
                      </label>
                      <input
                        type="time"
                        value={candidate.time}
                        onChange={(e) => updateCandidateSchedule(candidate.id, 'time', e.target.value)}
                        disabled={candidate.status === 'success'}
                        style={{
                          width: '100%',
                          padding: '10px 12px',
                          border: '2px solid #E0E0E0',
                          borderRadius: '8px',
                          fontSize: '13px',
                          fontFamily: 'Montserrat',
                          color: 'var(--app-heading, #031128)',
                          backgroundColor: candidate.status === 'success' ? '#F8F9FA' : '#FFFFFF',
                          boxSizing: 'border-box'
                        }}
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Status Message */}
          {scheduleStatus && (
            <div style={{
              padding: '16px 20px',
              borderRadius: '12px',
              marginBottom: '24px',
              backgroundColor: statusTone === 'error' || statusTone === 'warning' ? '#F8D7DA' :
                statusTone === 'success' ? '#D4EDDA' : '#FFF3CD',
              color: statusTone === 'error' || statusTone === 'warning' ? 'var(--app-tone-721c24, #721C24)' :
                statusTone === 'success' ? 'var(--app-tone-155724, #155724)' : 'var(--app-tone-856404, #856404)',
              border: `1px solid ${statusTone === 'error' || statusTone === 'warning' ? '#F5C6CB' :
                statusTone === 'success' ? '#C3E6CB' : '#FFEAA7'}`,
              fontSize: '14px',
              fontFamily: 'Montserrat',
              fontWeight: '600'
            }}>
              {scheduleStatus}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="modal-footer">
          <button className="btn-secondary" onClick={onClose} disabled={isScheduling}>
            {t('interviewScheduler.cancel')}
          </button>

          <button
            className="btn-primary"
            onClick={scheduleAllInterviews}
            disabled={isScheduling || calendarConnections.length === 0}
            style={{
              opacity: isScheduling || calendarConnections.length === 0 ? 0.6 : 1,
              cursor: isScheduling || calendarConnections.length === 0 ? 'not-allowed' : 'pointer'
            }}
          >
            {isScheduling ? (
              <>
                <i className="fa-solid fa-spinner fa-spin" style={{ marginRight: '8px' }}></i>
                {t('bulkInterview.scheduling')}
              </>
            ) : (
              <>
                <i className="fa-solid fa-calendar-check" style={{ marginRight: '8px' }}></i>
                {t('bulkInterview.scheduleAll', { count: candidateSchedules.length })}
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default BulkInterviewScheduler;
